import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Factory, Shield, Users, Award, Target, Truck, CheckCircle, Leaf, Globe, Cog } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const About = () => {
  const { t } = useTranslation();
  
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative min-h-[300px] sm:min-h-[400px] lg:min-h-[500px] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=1920&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-neutral-900/90 via-neutral-900/80 to-emerald-900/70"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 z-10 py-12 sm:py-16 lg:py-20">
          <span className="inline-block text-emerald-400 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-4">{t('about.subtitle')}</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 lg:mb-6">{t('about.title')}</h1>
          <p className="text-base sm:text-lg lg:text-xl text-neutral-200 max-w-3xl leading-relaxed">
            {t('about.description')}
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <div>
              <span className="inline-block text-emerald-600 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-3">{t('company.name')}</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 text-neutral-900">{t('about.whoWeAre')}</h2>
              <p className="text-neutral-600 text-sm sm:text-base lg:text-lg mb-4 sm:mb-6 leading-relaxed">
                {t('about.description')}
              </p>
              <p className="text-neutral-600 text-sm sm:text-base lg:text-lg leading-relaxed mb-4 sm:mb-6 lg:mb-8">
                {t('about.facilitiesDesc')}
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-4">
                <div className="flex items-center bg-emerald-50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                  <CheckCircle className="text-emerald-600 mr-1.5 sm:mr-2" size={16} />
                  <span className="text-xs sm:text-sm font-medium text-emerald-800">ISO 9001</span>
                </div>
                <div className="flex items-center bg-emerald-50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                  <CheckCircle className="text-emerald-600 mr-1.5 sm:mr-2" size={16} />
                  <span className="text-xs sm:text-sm font-medium text-emerald-800">ISO 14001</span>
                </div>
                <div className="flex items-center bg-emerald-50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                  <CheckCircle className="text-emerald-600 mr-1.5 sm:mr-2" size={16} />
                  <span className="text-xs sm:text-sm font-medium text-emerald-800">EN 840</span>
                </div>
              </div>
            </div>
            <div className="relative order-first lg:order-last">
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-3xl blur-2xl"></div>
              <img
                src="/assets/fabrika.png"
                alt={t('about.manufacturing')}
                className="relative rounded-xl sm:rounded-2xl shadow-2xl w-full aspect-video object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-neutral-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <span className="inline-block text-emerald-600 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-3">{t('about.values')}</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900">{t('whyChoose.subtitle')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            <Card className="text-center hover:shadow-2xl transition-all duration-300 border-0 shadow-lg group">
              <CardContent className="pt-6 sm:pt-8 lg:pt-10 pb-6 sm:pb-8 px-4 sm:px-6 lg:px-8">
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-600 mb-4 sm:mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-emerald-100">
                  <Shield className="w-6 h-6 sm:w-8 sm:h-8 lg:w-9 lg:h-9" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-2 sm:mb-4 text-neutral-900">{t('about.quality')}</h3>
                <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                  {t('about.qualityDesc')}
                </p>
              </CardContent>
            </Card>
            <Card className="text-center hover:shadow-2xl transition-all duration-300 border-0 shadow-lg group">
              <CardContent className="pt-6 sm:pt-8 lg:pt-10 pb-6 sm:pb-8 px-4 sm:px-6 lg:px-8">
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-600 mb-4 sm:mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-emerald-100">
                  <Target className="w-6 h-6 sm:w-8 sm:h-8 lg:w-9 lg:h-9" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-2 sm:mb-4 text-neutral-900">{t('about.innovation')}</h3>
                <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                  {t('about.innovationDesc')}
                </p>
              </CardContent>
            </Card>
            <Card className="text-center hover:shadow-2xl transition-all duration-300 border-0 shadow-lg group sm:col-span-2 md:col-span-1">
              <CardContent className="pt-6 sm:pt-8 lg:pt-10 pb-6 sm:pb-8 px-4 sm:px-6 lg:px-8">
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-600 mb-4 sm:mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-emerald-100">
                  <Users className="w-6 h-6 sm:w-8 sm:h-8 lg:w-9 lg:h-9" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-2 sm:mb-4 text-neutral-900">{t('about.customerFocus')}</h3>
                <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                  {t('about.customerFocusDesc')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Manufacturing Excellence */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <span className="inline-block text-emerald-600 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-3">{t('about.manufacturing')}</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900">{t('about.manufacturing')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-12">
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-600 mb-4 sm:mb-6 group-hover:scale-110 transition-transform shadow-xl shadow-emerald-100">
                <Factory className="w-7 h-7 sm:w-9 sm:h-9 lg:w-10 lg:h-10" />
              </div>
              <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-2 sm:mb-3 text-neutral-900">{t('about.facilities')}</h3>
              <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                {t('about.facilitiesDesc')}
              </p>
            </div>
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-600 mb-4 sm:mb-6 group-hover:scale-110 transition-transform shadow-xl shadow-emerald-100">
                <Award className="w-7 h-7 sm:w-9 sm:h-9 lg:w-10 lg:h-10" />
              </div>
              <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-2 sm:mb-3 text-neutral-900">{t('about.certified')}</h3>
              <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                {t('about.certifiedDesc')}
              </p>
            </div>
            <div className="text-center group sm:col-span-2 md:col-span-1">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-600 mb-4 sm:mb-6 group-hover:scale-110 transition-transform shadow-xl shadow-emerald-100">
                <Truck className="w-7 h-7 sm:w-9 sm:h-9 lg:w-10 lg:h-10" />
              </div>
              <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-2 sm:mb-3 text-neutral-900">{t('about.delivery')}</h3>
              <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                {t('about.deliveryDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Control Process */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-neutral-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-10 lg:mb-12">
              <span className="inline-block text-emerald-600 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-3">{t('about.quality')}</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900">{t('about.qualityProcess.title')}</h2>
            </div>
            <div className="space-y-4 sm:space-y-6 lg:space-y-8">
              <div className="flex items-start bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center font-bold text-base sm:text-lg lg:text-xl mr-4 sm:mr-6 shadow-lg shadow-emerald-500/30">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-1 sm:mb-2 text-neutral-900">{t('about.qualityProcess.step1.title')}</h3>
                  <p className="text-neutral-600 text-xs sm:text-sm lg:text-base leading-relaxed">
                    {t('about.qualityProcess.step1.description')}
                  </p>
                </div>
              </div>
              <div className="flex items-start bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center font-bold text-base sm:text-lg lg:text-xl mr-4 sm:mr-6 shadow-lg shadow-emerald-500/30">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-1 sm:mb-2 text-neutral-900">{t('about.qualityProcess.step2.title')}</h3>
                  <p className="text-neutral-600 text-xs sm:text-sm lg:text-base leading-relaxed">
                    {t('about.qualityProcess.step2.description')}
                  </p>
                </div>
              </div>
              <div className="flex items-start bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center font-bold text-base sm:text-lg lg:text-xl mr-4 sm:mr-6 shadow-lg shadow-emerald-500/30">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-1 sm:mb-2 text-neutral-900">{t('about.qualityProcess.step3.title')}</h3>
                  <p className="text-neutral-600 text-xs sm:text-sm lg:text-base leading-relaxed">
                    {t('about.qualityProcess.step3.description')}
                  </p>
                </div>
              </div>
              <div className="flex items-start bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center font-bold text-base sm:text-lg lg:text-xl mr-4 sm:mr-6 shadow-lg shadow-emerald-500/30">
                  4
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-1 sm:mb-2 text-neutral-900">{t('about.qualityProcess.step4.title')}</h3>
                  <p className="text-neutral-600 text-xs sm:text-sm lg:text-base leading-relaxed">
                    {t('about.qualityProcess.step4.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2ek0xOCA0MmMzLjMxNCAwIDYtMi42ODYgNi02cy0yLjY4Ni02LTYtNi02IDIuNjg2LTYgNiAyLjY4NiA2IDYgNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 text-center">
            <div className="group">
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-1 sm:mb-2 lg:mb-3 group-hover:scale-110 transition-transform">{t('about.stats.years')}</div>
              <div className="text-emerald-100 text-xs sm:text-sm lg:text-lg">{t('about.stats.yearsLabel')}</div>
            </div>
            <div className="group">
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-1 sm:mb-2 lg:mb-3 group-hover:scale-110 transition-transform">{t('about.stats.countries')}</div>
              <div className="text-emerald-100 text-xs sm:text-sm lg:text-lg">{t('about.stats.countriesLabel')}</div>
            </div>
            <div className="group">
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-1 sm:mb-2 lg:mb-3 group-hover:scale-110 transition-transform">{t('about.stats.bins')}</div>
              <div className="text-emerald-100 text-xs sm:text-sm lg:text-lg">{t('about.stats.binsLabel')}</div>
            </div>
            <div className="group">
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-1 sm:mb-2 lg:mb-3 group-hover:scale-110 transition-transform">{t('about.stats.quality')}</div>
              <div className="text-emerald-100 text-xs sm:text-sm lg:text-lg">{t('about.stats.qualityLabel')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 text-neutral-900">{t('cta.readyToOrder')}</h2>
          <p className="text-neutral-600 text-sm sm:text-base lg:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">{t('cta.description')}</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button asChild size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg shadow-emerald-500/25 px-6 sm:px-8 text-sm sm:text-base">
              <Link to="/products">{t('hero.viewCatalog')}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-6 sm:px-8 text-sm sm:text-base">
              <Link to="/contact">{t('products.contactUs')}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
